import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/common";
import { ResponsiveContainer, AreaChart, Area, LineChart, Line, XAxis, Tooltip, CartesianGrid } from "recharts";
import {
  BarChart3, Users, FileSearch, Inbox, Phone, Mail, Activity, WifiOff,
  TrendingUp, TrendingDown, Minus, ChevronDown,
} from "lucide-react";
import { useI18n } from "@/context/I18nContext";

const RANGES = [
  { key: "today", label: "Heute" }, { key: "7d", label: "7 Tage" }, { key: "30d", label: "30 Tage" },
  { key: "month", label: "Monat" }, { key: "quarter", label: "Quartal" }, { key: "year", label: "Jahr" },
];
const CHART_METRICS = [
  { key: "visitors", label: "Besucher" }, { key: "sessions", label: "Sitzungen" },
  { key: "pageviews", label: "Seitenaufrufe" }, { key: "forms", label: "Formulare" },
  { key: "phone", label: "Telefon" }, { key: "email", label: "E-Mail" },
];
const KPIS = [
  { key: "visitors", label: "Besucher" }, { key: "sessions", label: "Sitzungen" },
  { key: "pageviews", label: "Seitenaufrufe" }, { key: "forms", label: "Formulare" },
  { key: "phone", label: "Telefon-Klicks" }, { key: "email", label: "E-Mail-Klicks" },
  { key: "conversion", label: "Conversion-Rate", suffix: "%" },
];

const nf = (v) => (typeof v === "number" ? v.toLocaleString("de-CH") : v);

export default function AnalyticsBoard({ nav, className = "" }) {
  const { t } = useI18n();
  const [clientId, setClientId] = useState("all");
  const [range, setRange] = useState("30d");
  const [metric, setMetric] = useState("visitors");
  const [compare, setCompare] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    setLoading(true);
    api.get(`/admin/analytics?client_id=${clientId}&range=${range}`)
      .then(({ data }) => { if (active) setData(data); })
      .catch(() => { if (active) setData(null); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [clientId, range]);

  // ECHT-Regel: nur echte Daten anzeigen. Demo-Daten bleiben in der DB, werden aber ausgeblendet.
  const effective = !!data?.has_real;
  const badge = effective
    ? { dot: "bg-[#A3E635]", txt: "text-[#A3E635]", border: "border-[#A3E635]/30", label: "Live verbunden" }
    : { dot: "bg-gray-600", txt: "text-gray-500", border: "border-white/10", label: "Nicht verbunden" };

  const series = data?.series || [];
  const prev = data?.series_prev || [];
  const kpis = data?.kpis || {};
  const comparison = (data?.clients || []).filter((c) => !c.demo);

  return (
    <Card className={`p-5 flex flex-col ${className}`} data-testid="analytics-board">
      {/* Header */}
      <div className="flex items-start justify-between gap-3 flex-wrap mb-4">
        <div className="flex items-center gap-2">
          <BarChart3 className="w-4 h-4 text-[#A3E635]" />
          <div>
            <div className="text-sm font-bold text-white flex items-center gap-2">{t("Besucher / Analytics")}</div>
            <div className="text-[11px] text-gray-500 flex items-center gap-1.5 mt-0.5" data-testid="analytics-scope">
              <Users className="w-3 h-3" /> {data?.scope_label || t("Alle Kunden")}
              {data?.period_label && <span className="text-gray-600">· {data.period_label}</span>}
            </div>
          </div>
        </div>
        <span className={`inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full border ${badge.border} ${badge.txt}`} data-testid="analytics-badge">
          <span className={`w-1.5 h-1.5 rounded-full ${badge.dot}`} /> {t(badge.label)}
        </span>
      </div>

      {/* Steuerung: Kundenfilter + Zeitraum */}
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <div className="relative">
          <select value={clientId} onChange={(e) => setClientId(e.target.value)} data-testid="analytics-client-filter"
            className="appearance-none bg-white/[0.04] border border-white/10 rounded-lg text-xs text-gray-200 pl-3 pr-7 py-1.5 hover:border-white/20 focus:outline-none focus:border-[#A3E635]/50 max-w-[220px] truncate">
            <option value="all">{t("Alle Kunden")}</option>
            {(data?.client_options || []).map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
          </select>
          <ChevronDown className="w-3.5 h-3.5 text-gray-500 absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none" />
        </div>
        <div className="flex items-center rounded-lg border border-white/10 bg-white/[0.02] p-0.5" data-testid="analytics-range">
          {RANGES.map((r) => (
            <button key={r.key} onClick={() => setRange(r.key)} data-testid={`range-${r.key}`}
              className={`text-[11px] font-medium px-2.5 py-1 rounded-md transition-colors ${range === r.key ? "bg-[#A3E635] text-black" : "text-gray-400 hover:text-white"}`}>
              {t(r.label)}
            </button>
          ))}
        </div>
        <button onClick={() => setCompare((v) => !v)} data-testid="analytics-compare-toggle"
          className={`text-[11px] font-medium px-2.5 py-1.5 rounded-lg border transition-colors ${compare ? "border-[#A3E635]/40 text-[#A3E635] bg-[#A3E635]/[0.08]" : "border-white/10 text-gray-400 hover:text-white"}`}>
          {t("vs. Vorperiode")}
        </button>
      </div>

      {/* Prominentes Label: welcher Kunde wird angezeigt */}
      <div className="flex items-center gap-2 mb-4 px-3.5 py-2.5 rounded-lg bg-[#A3E635]/[0.06] border border-[#A3E635]/20" data-testid="analytics-shown-client">
        <Users className="w-4 h-4 text-[#A3E635] shrink-0" />
        <span className="text-xs text-gray-400">{t("Angezeigter Kunde:")}</span>
        <span className="text-sm font-bold text-white truncate">
          {clientId === "all"
            ? t("Alle Kunden")
            : ((data?.client_options || []).find((o) => o.id === clientId)?.name || data?.scope_label || t("Alle Kunden"))}
        </span>
        {data?.period_label && <span className="ml-auto text-[11px] text-gray-500 shrink-0">{data.period_label}</span>}
      </div>

      {/* Datenquelle + Site-Kit-Erkennung (ehrlich, Phase 1) */}
      <div className="flex flex-wrap items-center gap-2 mb-4" data-testid="analytics-source-row">
        <span className="inline-flex items-center gap-1.5 text-[11px] font-medium px-2.5 py-1 rounded-lg border border-white/10 bg-white/[0.02] text-gray-300" data-testid="analytics-data-source">
          <Activity className="w-3 h-3 text-gray-400" /> {t("Datenquelle:")} <span className="font-bold text-white">{(effective && data?.data_source) ? data.data_source : t("Nicht verbunden")}</span>
        </span>
        {data?.site_kit && (() => {
          const skc = { green: "border-[#A3E635]/40 text-[#A3E635] bg-[#A3E635]/[0.08]", amber: "border-amber-400/40 text-amber-300 bg-amber-400/10", gray: "border-white/10 text-gray-400 bg-white/[0.02]" };
          const dot = { green: "bg-[#A3E635]", amber: "bg-amber-400", gray: "bg-gray-600" };
          const c = data.site_kit.color || "gray";
          return (
            <span className={`inline-flex items-center gap-1.5 text-[11px] font-medium px-2.5 py-1 rounded-lg border ${skc[c] || skc.gray}`} data-testid="analytics-sitekit">
              <span className={`w-1.5 h-1.5 rounded-full ${dot[c] || dot.gray}`} /> Site Kit: <span className="font-bold">{data.site_kit.label}</span>
            </span>
          );
        })()}
      </div>

      {!effective ? (
        <NotConnected nav={nav} />
      ) : (
        <div className="flex flex-col flex-1">
          {/* KPI-Zeile */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5" data-testid="analytics-kpis">
            {KPIS.map((k) => <Kpi key={k.key} label={t(k.label)} data={kpis[k.key]} suffix={k.suffix} />)}
          </div>

          {/* Metrik-Umschaltung */}
          <div className="flex flex-wrap items-center gap-1.5 mt-4 mb-1" data-testid="analytics-metric-toggle">
            {CHART_METRICS.map((m) => (
              <button key={m.key} onClick={() => setMetric(m.key)} data-testid={`metric-${m.key}`}
                className={`text-[11px] font-medium px-2.5 py-1 rounded-full border transition-colors ${metric === m.key ? "border-[#A3E635]/50 text-[#A3E635] bg-[#A3E635]/[0.1]" : "border-white/10 text-gray-400 hover:text-white"}`}>
                {t(m.label)}
              </button>
            ))}
          </div>

          {/* Hauptgrafik */}
          <MetricChart series={series} prev={prev} metric={metric} compare={compare} loading={loading} />

          {/* Vier Detailblöcke */}
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3 mt-4">
            <DataBox icon={FileSearch} title={t("Top-Seiten")} testid="box-top-pages">
              {(data?.top_pages || []).length ? data.top_pages.slice(0, 5).map((p, i) => (
                <RowKV key={i} k={p.path || p.page} v={nf(p.views)} />
              )) : <Muted>{t("Keine Daten")}</Muted>}
            </DataBox>
            <DataBox icon={Inbox} title={t("Formular-Übermittlungen")} testid="box-forms">
              {(data?.forms || []).filter((f) => f.count > 0).length ? data.forms.filter((f) => f.count > 0).map((f, i) => (
                <RowKV key={i} k={f.name} v={nf(f.count)} sub={f.source} />
              )) : <Muted>{t("Keine Übermittlungen")}</Muted>}
            </DataBox>
            <DataBox icon={Phone} title={t("Telefon-Klicks")} testid="box-phone">
              {(data?.phone_targets || []).filter((x) => x.clicks > 0).length ? data.phone_targets.filter((x) => x.clicks > 0).map((x, i) => (
                <RowKV key={i} k={x.target} v={nf(x.clicks)} sub={clientId === "all" ? x.client : null} />
              )) : <Muted>{t("Keine Klicks")}</Muted>}
            </DataBox>
            <DataBox icon={Mail} title={t("E-Mail-Klicks")} testid="box-email">
              {(data?.email_targets || []).filter((x) => x.clicks > 0).length ? data.email_targets.filter((x) => x.clicks > 0).map((x, i) => (
                <RowKV key={i} k={x.target} v={nf(x.clicks)} sub={clientId === "all" ? x.client : null} />
              )) : <Muted>{t("Keine Klicks")}</Muted>}
            </DataBox>
          </div>

          {/* Kundenvergleich */}
          <div className="mt-5" data-testid="analytics-comparison">
            <div className="flex items-center gap-1.5 mb-2 text-[11px] font-bold uppercase tracking-wider text-gray-400">
              <Users className="w-3.5 h-3.5 text-[#A3E635]" /> {t("Kundenvergleich · {x}").replace("{x}", data?.range_label || "")}
            </div>
            <div className="overflow-x-auto rounded-xl border border-white/10">
              <table className="w-full text-xs">
                <thead>
                  <tr className="text-left text-gray-500 border-b border-white/10 bg-white/[0.02]">
                    <th className="font-medium py-2 px-3">{t("Kunde")}</th>
                    <th className="font-medium py-2 px-2 text-right">{t("Besucher")}</th>
                    <th className="font-medium py-2 px-2 text-right">{t("Form.")}</th>
                    <th className="font-medium py-2 px-2 text-right hidden sm:table-cell">{t("Tel.")}</th>
                    <th className="font-medium py-2 px-2 text-right hidden sm:table-cell">{t("Mail")}</th>
                    <th className="font-medium py-2 px-2 hidden md:table-cell">{t("Top-Seite")}</th>
                    <th className="font-medium py-2 px-2 text-right">{t("Trend")}</th>
                    <th className="font-medium py-2 px-3">{t("Status")}</th>
                  </tr>
                </thead>
                <tbody>
                  {comparison.map((c) => (
                    <tr key={c.id} onClick={() => setClientId(c.id)} data-testid={`compare-row-${c.id}`}
                      className="border-b border-white/5 last:border-0 hover:bg-white/[0.03] cursor-pointer">
                      <td className="py-2 px-3 text-gray-200 font-medium truncate max-w-[160px]">{c.name}</td>
                      <td className="py-2 px-2 text-right text-white font-semibold">{nf(c.visitors)}</td>
                      <td className="py-2 px-2 text-right text-gray-300">{nf(c.forms)}</td>
                      <td className="py-2 px-2 text-right text-gray-300 hidden sm:table-cell">{nf(c.phone)}</td>
                      <td className="py-2 px-2 text-right text-gray-300 hidden sm:table-cell">{nf(c.email)}</td>
                      <td className="py-2 px-2 text-gray-400 truncate max-w-[120px] hidden md:table-cell">{c.top_page}</td>
                      <td className="py-2 px-2 text-right"><Delta v={c.delta_pct} /></td>
                      <td className="py-2 px-3"><StatusPill status={c.status} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="mt-3 text-[10px] text-gray-600 flex items-center gap-1.5 flex-wrap">
            <Activity className="w-3 h-3" /> {t("Quelle: {x}").replace("{x}", data?.source || "")}
            {data?.last_sync && <span>· {t("Stand: {x}").replace("{x}", new Date(data.last_sync).toLocaleString("de-CH", { dateStyle: "short", timeStyle: "short" }))}</span>}
            <span className="text-gray-700">· {t("Besucher/Seiten: GA4 / Analytics Light · Formulare: WP-Connector · Klicks: tel:/mailto:-Tracking")}</span>
          </div>
        </div>
      )}
    </Card>
  );
}

function Kpi({ label, data = {}, suffix = "" }) {
  const v = data?.value ?? 0;
  return (
    <div className="rounded-xl border border-white/10 bg-white/[0.02] p-3" data-testid={`kpi-${label}`}>
      <div className="text-[10px] uppercase tracking-wider text-gray-500 truncate">{label}</div>
      <div className="text-xl font-black text-white mt-0.5">{nf(v)}{suffix}</div>
      <div className="mt-1"><Delta v={data?.delta_pct} small /></div>
    </div>
  );
}

function Delta({ v, small }) {
  if (v === undefined || v === null) return <span className="text-gray-600 text-[11px]">—</span>;
  const up = v > 0, flat = v === 0;
  const Icon = flat ? Minus : up ? TrendingUp : TrendingDown;
  const col = flat ? "text-gray-500" : up ? "text-[#A3E635]" : "text-red-400";
  return (
    <span className={`inline-flex items-center gap-0.5 ${col} ${small ? "text-[10px]" : "text-[11px]"} font-semibold`}>
      <Icon className="w-3 h-3" /> {v > 0 ? "+" : ""}{v}%
    </span>
  );
}

function StatusPill({ status }) {
  const { t } = useI18n();
  const map = {
    gut: { c: "text-[#A3E635] border-[#A3E635]/30 bg-[#A3E635]/[0.08]", l: "Läuft gut" },
    stabil: { c: "text-gray-400 border-white/10 bg-white/[0.03]", l: "Stabil" },
    achtung: { c: "text-amber-300 border-amber-400/30 bg-amber-400/[0.08]", l: "Achtung" },
  };
  const s = map[status] || map.stabil;
  return <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full border ${s.c}`}>{t(s.l)}</span>;
}

function MetricChart({ series = [], prev = [], metric, compare, loading }) {
  const { t } = useI18n();
  const data = series.map((row, i) => ({ d: row.date, cur: row[metric] || 0, prev: (prev[i] || {})[metric] || 0 }));
  if (loading) return <div className="mt-3 h-[160px] rounded-xl border border-white/10 bg-white/[0.02] flex items-center justify-center text-xs text-gray-600">{t("Lädt…")}</div>;
  if (!data.length) return <div className="mt-3 h-[160px] rounded-xl border border-white/10 bg-white/[0.02] flex items-center justify-center text-xs text-gray-600">{t("Keine Verlaufsdaten")}</div>;
  const Tip = ({ active, payload }) => {
    if (!active || !payload?.length) return null;
    const p = payload[0].payload;
    const [y, m, dd] = (p.d || "").split("-");
    return (
      <div className="rounded-lg bg-[#0A0A0C] border border-white/15 px-3 py-1.5 text-xs">
        <div className="text-gray-400">{dd}.{m}.{y}</div>
        <div className="text-[#A3E635] font-bold">{nf(p.cur)}</div>
        {compare && <div className="text-gray-500">{t("Vorperiode: {x}").replace("{x}", nf(p.prev))}</div>}
      </div>
    );
  };
  return (
    <div className="mt-3 h-[160px] -mx-1" data-testid="analytics-chart">
      <ResponsiveContainer width="100%" height="100%" minHeight={160} debounce={1}>
        <AreaChart data={data} margin={{ top: 6, right: 6, left: 6, bottom: 0 }}>
          <defs>
            <linearGradient id="ab-grad" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#A3E635" stopOpacity={0.45} />
              <stop offset="100%" stopColor="#A3E635" stopOpacity={0} />
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="3 3" stroke="#ffffff" strokeOpacity={0.05} vertical={false} />
          <XAxis dataKey="d" hide />
          <Tooltip content={<Tip />} cursor={{ stroke: "#A3E635", strokeOpacity: 0.25 }} />
          {compare && <Area type="monotone" dataKey="prev" stroke="#6b7280" strokeWidth={1.5} strokeDasharray="4 3" fill="none" dot={false} />}
          <Area type="monotone" dataKey="cur" stroke="#A3E635" strokeWidth={2} fill="url(#ab-grad)" dot={false} activeDot={{ r: 3, fill: "#A3E635" }} />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
}

function NotConnected({ nav }) {
  const { t } = useI18n();
  const bars = Array.from({ length: 30 }, (_, i) => 20 + ((i * 37) % 60));
  return (
    <div className="flex flex-col flex-1" data-testid="analytics-not-connected">
      <div className="relative h-[180px] rounded-xl border border-white/10 bg-white/[0.02] overflow-hidden">
        <div className="absolute inset-0 flex items-end gap-1 px-3 opacity-20">
          {bars.map((h, i) => <div key={i} className="flex-1 bg-gray-500 rounded-t" style={{ height: `${h}%` }} />)}
        </div>
        <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-4 bg-[#0A0A0C]/70">
          <WifiOff className="w-7 h-7 text-gray-500 mb-2" />
          <div className="text-sm font-semibold text-gray-200">{t("Analytics noch nicht verbunden")}</div>
          <div className="text-[11px] text-gray-500 mt-1 max-w-[340px]">{t("KEWAGPage Analytics Light oder GA4 verbinden, um echte Besucher, Top-Seiten, Formulare und Klicks zu sehen.")}</div>
          <button onClick={() => nav("/admin/settings")} data-testid="connect-analytics-btn"
            className="mt-3 bg-[#A3E635] hover:bg-[#BEF264] text-black font-bold text-xs py-2 px-4 rounded-lg inline-flex items-center gap-2"><Activity className="w-3.5 h-3.5" /> {t("Analytics verbinden")}</button>
        </div>
      </div>
    </div>
  );
}

function DataBox({ icon: Icon, title, children, testid }) {
  return (
    <div className="rounded-xl border border-white/10 bg-white/[0.02] p-3" data-testid={testid}>
      <div className="flex items-center gap-1.5 mb-2 text-[11px] font-bold uppercase tracking-wider text-gray-400">
        <Icon className="w-3.5 h-3.5 text-[#A3E635]" /> {title}
      </div>
      <div className="space-y-1.5">{children}</div>
    </div>
  );
}

const RowKV = ({ k, v, sub }) => (
  <div className="flex items-center justify-between gap-2 text-xs">
    <span className="min-w-0">
      <span className="block truncate text-gray-300">{k}</span>
      {sub && <span className="block truncate text-[10px] text-gray-600">{sub}</span>}
    </span>
    <span className="font-semibold text-white shrink-0">{v}</span>
  </div>
);

const Muted = ({ children }) => <div className="text-[11px] text-gray-600 py-1">{children}</div>;
